import * as React from 'react';

import { translate } from '~l10n/client';

import { Action } from '~shared/components/ActionMenu';
import { IconAnswerKey } from '~shared/components/icons/standard';
import { useWorkspacePermissionsV2 } from '~teacher/hook/workspaces';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';

interface UseExportAnswerKeyActionParams {
  workspaceId: string;
  paywallCallback: () => void;
}

export function useExportAnswerKeyAction({
  paywallCallback,
  workspaceId,
}: UseExportAnswerKeyActionParams) {
  const {
    permissions: { downloadQuiz: canDownloadQuiz },
  } = useWorkspacePermissionsV2({ workspaceId });
  
  const [isAnswerKeyDialogOpen, setIsAnswerKeyDialogOpen] = React.useState(false);
  const [selectedQuizId, setSelectedQuizId] = React.useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = React.useState<string | null>(null);
  
  const exportAnswerKeyAction = React.useMemo(
    () =>
      ({
        key: 'export-answer-key',
        title: translate('Export Answer Key'),
        icon: <IconAnswerKey />,
        action: ({ data, close }) => {
          const firstItem = data[0];
          if (firstItem.type === 'quiz') {
            const { id, workspaceId } = firstItem;
            setSelectedQuizId(id);
            setSelectedWorkspaceId(workspaceId);
            setIsAnswerKeyDialogOpen(true);
            close();
          }
        },
        constraint: ({ data: quizzes }: { data: WorkspaceItem[] }) => {
          return quizzes.every((q) => q.type === 'quiz');
        },
        showProBadge: !canDownloadQuiz,
        disabled: ({ data: quizzes }) => quizzes.length > 1 || !canDownloadQuiz,
        paywallCallback: !canDownloadQuiz ? paywallCallback : null,
      } as Action<WorkspaceItem>),
    [canDownloadQuiz, paywallCallback]
  );

  return {
    exportAnswerKeyAction,
    isAnswerKeyDialogOpen,
    setIsAnswerKeyDialogOpen,
    selectedQuizId,
    selectedWorkspaceId,
  };
}
